import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, useTrail, animated } from 'react-spring';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useInterval from '@/hooks/use-interval';

const IMAGES = {
  sheet: '/images/understanding-layout-algorithms/sheet-base.png',
  blockLabel:
    '/images/understanding-layout-algorithms/block-label.png',
  inlineLabel:
    '/images/understanding-layout-algorithms/inline-label.png',
  pinkBox1: '/images/understanding-layout-algorithms/pink-box-1.png',
  pinkBox2: '/images/understanding-layout-algorithms/pink-box-2.png',
  pinkBox3: '/images/understanding-layout-algorithms/pink-box-3.png',
  pinkBox4: '/images/understanding-layout-algorithms/pink-box-4.png',
  purpleCircle1:
    '/images/understanding-layout-algorithms/purple-circle-1.png',
  purpleCircle2:
    '/images/understanding-layout-algorithms/purple-circle-2.png',
  purpleCircle3:
    '/images/understanding-layout-algorithms/purple-circle-3.png',
  purpleCircle4:
    '/images/understanding-layout-algorithms/purple-circle-4.png',
  purpleCircle5:
    '/images/understanding-layout-algorithms/purple-circle-5.png',
  purpleCircle6:
    '/images/understanding-layout-algorithms/purple-circle-6.png',
  purpleCircle7:
    '/images/understanding-layout-algorithms/purple-circle-7.png',
  purpleCircle8:
    '/images/understanding-layout-algorithms/purple-circle-8.png',
};

/**
 * The stages are:
 * - `initial`: blank sheet
 * - `add-blocks`: Show the 4 pink boxes, staggered
 * - `label-blocks`: Show the "Block" label
 * - `intermission-a`: Briefly clear pink boxes and label
 * - `intermission-b`: Zoom in to the first paragraph
 * - `add-inline`: Show the 9 purple circles, staggered
 * - `label-inline`: Show the "Inline" label
 */
const STAGES = [
  'initial',
  'add-blocks',
  'label-blocks',
  'intermission-a',
  'intermission-b',
  'add-inline',
  'label-inline',
];
const STAGE_DELAYS = {
  initial: 1000,
  'add-blocks': 500,
  'label-blocks': 2250,
  'intermission-a': 750,
  'intermission-b': 750,
  'add-inline': 600,
  'label-inline': 2250,
};

const BlocksInline = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [stage, setStage] = React.useState('initial');

  useInterval(() => {
    const currentIndex = STAGES.indexOf(stage);
    const nextIndex = (currentIndex + 1) % STAGES.length;

    setStage(STAGES[nextIndex]);
  }, STAGE_DELAYS[stage]);

  const showBlocksGroup =
    stage === 'add-blocks' || stage === 'label-blocks';
  const showInlineGroup =
    stage === 'add-inline' || stage === 'label-inline';

  const blockLabelStyle = useSpring({
    from: {
      transform: `translateY(-20px)`,
      clipPath: `polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)`,
    },
    to: {
      transform: `translateY(0px)`,
      clipPath: `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`,
    },
    reset: stage === 'label-blocks',
    immediate: prefersReducedMotion,
    config: {
      tension: 300,
      friction: 60,
    },
  });

  const blockTrail = useTrail(4, {
    transform:
      showBlocksGroup || stage === 'intermission-a'
        ? `translateY(0px)`
        : `translateY(-20px)`,
    opacity: showBlocksGroup || stage === 'intermission-a' ? 1 : 0,
    immediate: prefersReducedMotion,
    config: {
      tension: 300,
      friction: 40,
    },
  });

  const inlineLabelStyle = useSpring({
    from: {
      transform: `translateX(-20px)`,
      clipPath: `polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)`,
    },
    to: {
      transform: `translateY(0px)`,
      clipPath: `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`,
    },
    reset: stage === 'label-inline',
    immediate: prefersReducedMotion,
    config: {
      tension: 300,
      friction: 60,
    },
  });

  const inlineTrail = useTrail(8, {
    transform:
      showInlineGroup || stage === 'initial'
        ? `translateX(0px) scale(1)`
        : `translateX(-4px) scale(0)`,
    opacity: showInlineGroup || stage === 'initial' ? 1 : 0,
    immediate: prefersReducedMotion,
    config: {
      tension: 300,
      friction: 20,
    },
  });

  const zoomSpring = useSpring({
    transform:
      showInlineGroup || stage === 'intermission-b'
        ? `scale(2)`
        : `scale(1)`,
    config: {
      tension: 300,
      friction: 60,
    },
  });

  return (
    <Wrapper>
      <InnerWrapper style={zoomSpring}>
        <BaseSheet
          alt="The same page as earlier, but with animated annotations. Labels show the block direction (vertical) and the inline direction (horizontal)."
          src={IMAGES.sheet}
        />
        <Group style={{ opacity: showBlocksGroup ? 1 : 0 }}>
          {stage !== 'add-blocks' && (
            <BlockLabel
              alt=""
              src={IMAGES.blockLabel}
              style={blockLabelStyle}
            />
          )}
          {blockTrail.map((props, index) => {
            const Block = BlockElems[index];
            const src = IMAGES[`pinkBox${index + 1}`];

            return (
              <Block key={index} alt="" src={src} style={props} />
            );
          })}
        </Group>
        <Group style={{ opacity: showInlineGroup ? 1 : 0 }}>
          {stage !== 'add-inline' && (
            <InlineLabel
              alt=""
              src={IMAGES.inlineLabel}
              style={inlineLabelStyle}
            />
          )}
          {inlineTrail.map((props, index) => {
            const InlineElem = InlineElems[index];
            const src = IMAGES[`purpleCircle${index + 1}`];

            return (
              <InlineElem
                key={index}
                alt=""
                src={src}
                style={props}
              />
            );
          })}
        </Group>
      </InnerWrapper>
    </Wrapper>
  );
};

const INTRINSIC_SHEET_WIDTH = 1203;

const Wrapper = styled.div`
  margin: 48px 0px;
  overflow: hidden;
`;

const InnerWrapper = styled(animated.div)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  transform-origin: 30% 20%;
`;

const BaseSheet = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
`;

const Group = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transition: opacity 500ms;
`;

const BlockLabel = styled(animated.img)`
  position: absolute;
  top: 10%;
  left: 0;
  width: ${(60 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;

const Block = styled(animated.img)`
  position: absolute;
  left: 16.5%;
`;

const Block1 = styled(Block)`
  top: 8.5%;
  width: ${(259 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Block2 = styled(Block)`
  top: 21%;
  width: ${(800 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Block3 = styled(Block)`
  top: 36%;
  width: ${(787 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Block4 = styled(Block)`
  top: 62.5%;
  width: ${(765 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;

const BlockElems = [Block1, Block2, Block3, Block4];

const InlineLabel = styled(animated.img)`
  position: absolute;
  top: 16%;
  left: 19%;
  width: ${(419 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;

const Inline = styled(animated.img)`
  position: absolute;
  top: 22.25%;
  backface-visibility: hidden;
`;

const Inline1 = styled(Inline)`
  top: 22.25%;
  left: 17%;
  width: ${(80 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline2 = styled(Inline)`
  left: 21.3%;
  width: ${(75 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline3 = styled(Inline)`
  left: 25%;
  width: ${(62 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline4 = styled(Inline)`
  left: 28%;
  width: ${(65 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline5 = styled(Inline)`
  top: 22%;
  left: 31.25%;
  width: ${(90 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline6 = styled(Inline)`
  left: 37%;
  width: ${(58 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline7 = styled(Inline)`
  left: 40%;
  top: 21.7%;
  width: ${(89 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;
const Inline8 = styled(Inline)`
  left: 45%;
  width: ${(80 / INTRINSIC_SHEET_WIDTH) * 100}%;
`;

const InlineElems = [
  Inline1,
  Inline2,
  Inline3,
  Inline4,
  Inline5,
  Inline6,
  Inline7,
  Inline8,
];

export default BlocksInline;
